import { FC, ReactNode, RefObject, useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";

dayjs.extend(duration);
dayjs.extend(utc);
import {
	Navigate,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";

import {
	type ChecklistFontScale,
	ChecklistViewProvider,
	useChecklistView,
} from "./ChecklistViewContext";
import { HeaderTools } from "./types";
import { AdminProtected } from "@/app/application/AdminProtected";
import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { SATOSLink } from "@/app/application/SATOSLink";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { useFetchChecklist } from "@/features/procedures/api/checklist.queries";
import {
	useFetchActiveProcedureDetails,
	useHandleProcedureDetails,
} from "@/features/procedures/api/procedure.queries";
import { useGetChecklistWithLiveData } from "@/features/procedures/hooks/useChecklistLiveData";
import {
	getChecklistProgress,
	isHeader,
	validSchemeId,
} from "@/features/procedures/lib/checklist";
import { ChecklistProcedureType } from "@/features/procedures/model/types/checklist";
import { CheckpointType } from "@/features/procedures/model/types/manualChecklist";
import { ProcedureDetailNode } from "@/features/procedures/model/types/node";
import { ChecklistNavigationControls } from "@/features/procedures/ui/components/checklists/checklist/ChecklistNavigationControls";
import { ChecklistRow } from "@/features/procedures/ui/components/checklists/checklist/ChecklistRow";
import {
	AccessTime as AccessTimeIcon,
	Close as CloseIcon,
	CloseOutlined as CloseOutlinedIcon,
	Edit as EditIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	FastForward as FastForwardIcon,
	FastRewind as FastRewindIcon,
	Fullscreen as FullscreenIcon,
	OpenInNew as NewWindowIcon,
	Print as PrintIcon,
	TextDecrease as TextDecreaseIcon,
	TextIncrease as TextIncreaseIcon,
} from "@mui/icons-material";
import {
	Box,
	Chip,
	Collapse,
	Divider,
	IconButton,
	Paper,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { RouteParams } from "@/service/Utilities";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

const manualChecklistGridColumns = {
	xs: "40px minmax(0, 1fr) 88px",
	md: "48px minmax(0, 1fr) minmax(140px, 220px) 56px 104px",
};

/* ────────────────────────────────────────────────────────── */
/* Shell wrapper                                              */
/* ────────────────────────────────────────────────────────── */

interface ChecklistProps {
	hide?: boolean;
	children?: ReactNode;
}

export const Checklist: FC<ChecklistProps> = ({ hide, children }) => {
	if (hide) return null;

	return (
		<ChecklistViewProvider>
			<Box
				sx={{
					bgcolor: "background.default",
					borderRadius: 2,
					display: "flex",
					flexDirection: "column",
					flex: "1 1 0%",
					minHeight: 0,
					overflow: "hidden",
				}}
			>
				{children}
			</Box>
		</ChecklistViewProvider>
	);
};

/** When expanded: header scrolls with content. When collapsed: header stays at top, only body scrolls. */
interface ChecklistProcedureLayoutProps {
	scrollRef: RefObject<HTMLDivElement | null>;
	header: ReactNode;
	children: ReactNode;
}

/** Wrapper that takes remaining flex space; scroll area inside uses position absolute to fill it (reliable scroll). */
const scrollWrapperSx = {
	position: "relative" as const,
	flex: "1 1 0%",
	minHeight: 0,
	overflow: "hidden",
};

const scrollBoxSx = {
	position: "absolute" as const,
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	overflowY: "auto" as const,
};

export const ChecklistProcedureLayout: FC<ChecklistProcedureLayoutProps> = ({
	scrollRef,
	header,
	children,
}) => {
	const { headerCollapsed } = useChecklistView();

	if (headerCollapsed) {
		return (
			<>
				{header}
				<Box sx={scrollWrapperSx}>
					<Box
						ref={scrollRef}
						className="checklist-body-scroll"
						sx={scrollBoxSx}
					>
						{children}
					</Box>
				</Box>
			</>
		);
	}

	return (
		<Box sx={scrollWrapperSx}>
			<Box ref={scrollRef} className="checklist-body-scroll" sx={scrollBoxSx}>
				{header}
				{children}
			</Box>
		</Box>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Body                                                       */
/* ────────────────────────────────────────────────────────── */

interface ChecklistBodyProps {
	activeSchemeId: number | undefined;
	editMode: boolean | undefined;
	materialHandling?: boolean;
}

export const ChecklistBody: FC<ChecklistBodyProps> = ({
	activeSchemeId,
	materialHandling,
	editMode,
}) => {
	const theme = useTheme();
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id ?? "0", 10);
	const { isLoading: isLoadingChecklist, data: checklist } =
		useGetChecklistWithLiveData(activeSchemeId ?? 0, procedureId);

	if (editMode) {
		return <></>;
	}

	const isLoading = isLoadingChecklist;
	const missingChecklist = !isLoadingChecklist && !checklist;
	const steps = checklist?.Steps ?? [];

	return (
		<section
			className="procedure-wrapper checklist-view"
			data-tour="checklist-body"
		>
			<Box
				className="procedure-wrapper-inner"
				sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}
			>
				<Box
					className={checklist?.ImportedChecklist ? "grid-wrapper" : undefined}
					sx={
						checklist?.ImportedChecklist
							? undefined
							: { display: "flex", flexDirection: "column", gap: 2 }
					}
				>
					<ManualHeading activeSchemeId={activeSchemeId} />

					{/* Loading state */}
					{isLoading && (
						<Paper
							elevation={0}
							sx={{
								p: 2,
								border: `1px solid ${theme.palette.divider}`,
								borderRadius: 2,
							}}
						>
							{Array.from({ length: 4 }, (_, i) => (
								<Box key={i} sx={{ mb: 2 }}>
									<Skeleton variant="text" height={28} width="60%" />
									<Skeleton
										variant="rectangular"
										height={56}
										sx={{ mt: 1, borderRadius: 1 }}
									/>
								</Box>
							))}
						</Paper>
					)}

					{missingChecklist && (
						<Paper
							elevation={0}
							sx={{
								p: 6,
								textAlign: "center",
								border: (t) => `1px dashed ${t.palette.divider}`,
								borderRadius: 2,
								color: "text.secondary",
								bgcolor: (t) =>
									alpha(
										t.palette.primary.main,
										t.palette.mode === "dark" ? 0.04 : 0.02,
									),
							}}
						>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{ mb: 1, color: "text.secondary" }}
							>
								<Translate String="Procedure.Checklist.Missing">
									No checklist has been created for this node yet
								</Translate>
							</Typography>
							<Typography variant="body2">
								<Translate String="Procedure.Checklist.MissingHint">
									Use the node actions to create a checklist or edit the node.
								</Translate>
							</Typography>
						</Paper>
					)}

					{/* Empty state */}
					{!isLoading && checklist && !checklist.Steps?.length && (
						<Paper
							elevation={0}
							sx={{
								p: 6,
								textAlign: "center",
								border: (t) => `1px dashed ${t.palette.divider}`,
								borderRadius: 2,
								color: "text.secondary",
								bgcolor: (t) =>
									alpha(
										t.palette.primary.main,
										t.palette.mode === "dark" ? 0.04 : 0.02,
									),
							}}
						>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{ mb: 1, color: "text.secondary" }}
							>
								<Translate String="Procedure.Checklist.Empty">
									No steps yet
								</Translate>
							</Typography>
							<Typography variant="body2">
								<Translate String="Procedure.Checklist.EmptyHint">
									Add steps to this checklist to get started.
								</Translate>
							</Typography>
						</Paper>
					)}

					{/* Table-like header: Nr | Aksjon | Ansvar | Kommentar | Sign (manual only – imported has own structure) */}
					{!isLoading &&
						!checklist?.ImportedChecklist &&
						steps.some(
							(s) => s.CheckpointType !== CheckpointType.Info && !isHeader(s),
						) && (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: manualChecklistGridColumns,
									columnGap: { xs: 1, md: 1.5 },
									alignItems: "center",
									px: { xs: 1.5, md: 2.5 },
									py: 1.25,
									borderBottom: 1,
									borderColor: "divider",
									typography: "caption",
									fontWeight: 700,
									color: "text.secondary",
									letterSpacing: "0.45px",
									textTransform: "uppercase",
								}}
							>
								<Box sx={{ textAlign: "right" }}>
									<Translate String="EditChecklist.Step.Trinn">Nr</Translate>
								</Box>
								<Box>
									<Translate String="EditChecklist.Step.StepTitle">
										Aksjon
									</Translate>
								</Box>
								<Box sx={{ display: { xs: "none", md: "block" } }}>
									<Translate String="Procedure.Checklist.Step.Responsibility">
										Ansvar
									</Translate>
								</Box>
								<Box sx={{ display: { xs: "none", md: "block" } }}>&nbsp;</Box>
								<Box sx={{ textAlign: "center" }}>
									<Translate String="Checklist.Step.Sign">Sign</Translate>
								</Box>
							</Box>
						)}

					{/* Steps - table-like cards with aligned columns */}
					{!isLoading &&
						steps.map((step) => (
							<Box key={step.Id}>
								<ChecklistRow
									showCommentsAndSuggestion
									activeSchemeId={activeSchemeId}
									checklist={checklist}
									materialHandling={materialHandling}
									step={step}
									isProcedurePage
								/>
							</Box>
						))}
				</Box>
			</Box>
		</section>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Section label (“Activity”)                                 */
/* ────────────────────────────────────────────────────────── */

interface ManualHeadingProps {
	activeSchemeId: number | undefined;
}

export const ManualHeading: FC<ManualHeadingProps> = ({ activeSchemeId }) => {
	const { data: ProcedureDetails } = useFetchActiveProcedureDetails();
	const { data: checklist } = useFetchChecklist(activeSchemeId ?? 0);
	const { activeChildNode } = useHandleProcedureDetails(
		ProcedureDetails?.ProcedureDetailNodes,
		activeSchemeId ?? 0,
	);
	const { bodyFontSize } = useChecklistView();

	if (!activeChildNode?.ImportedChecklist && checklist) {
		return (
			<Typography
				variant="overline"
				sx={{
					letterSpacing: 1.1,
					fontSize: bodyFontSize,
					color: "text.secondary",
					display: "block",
					mb: 0.5,
				}}
			>
				<Translate String="Procedure.Checklist.ManualHeading">
					Activity
				</Translate>
			</Typography>
		);
	}

	return <></>;
};

/* ────────────────────────────────────────────────────────── */
/* Checklist Header (title + ingress)                         */
/* ────────────────────────────────────────────────────────── */

/** Compact progress bar: thin bar + "Progress: X / Y steps completed", theme-aware */
const CompactProgressBar: FC<{
	completed: number;
	total: number;
	bodyFontSize: string;
	showLabel?: boolean;
}> = ({ completed, total, bodyFontSize, showLabel = true }) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const completionRatio = total ? completed / total : 0;
	const trackColor =
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.white, 0.12)
			: alpha(theme.palette.common.black, 0.08);
	const progressLabel = GetLanguageValueAsString(
		translation,
		"Procedure.Checklist.Progress.Label",
		"Progress: {0} / {1} steps completed",
	)
		.replace("{0}", String(completed))
		.replace("{1}", String(total));
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.25,
				minWidth: 0,
				flex: 1,
				px: 1.25,
				py: 1,
				backgroundColor:
					theme.palette.mode === "dark"
						? alpha(theme.palette.background.paper, 0.55)
						: alpha(theme.palette.background.default, 0.7),
			}}
		>
			<Box
				sx={{
					flex: 1,
					minWidth: 0,
					height: 8,
					borderRadius: 999,
					bgcolor: trackColor,
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						width: `${total ? (100 * completed) / total : 0}%`,
						height: "100%",
						background:
							completionRatio >= 1
								? theme.palette.success.main
								: alpha(theme.palette.success.main, 0.85),
						borderRadius: 999,
						transition: "width 0.3s ease",
					}}
				/>
			</Box>
			<Box
				component="span"
				sx={{
					typography: "caption",
					fontWeight: 700,
					color: "text.primary",
					fontSize: bodyFontSize,
					flexShrink: 0,
				}}
			>
				{showLabel ? progressLabel : `${completed}/${total}`}
			</Box>
		</Box>
	);
};

interface ChecklistHeaderProps {
	editMode: boolean | undefined;
	activeSchemeId: number | undefined;
	children?: ReactNode;
}

export const ChecklistHeader: FC<ChecklistHeaderProps> = ({
	activeSchemeId,
	editMode,
	children,
}) => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id ?? "0", 10);
	const { data: checklist, isLoading } = useGetChecklistWithLiveData(
		activeSchemeId ?? 0,
		procedureId,
	);
	const { bodyFontSize, headingFontSize, headerCollapsed } = useChecklistView();

	const progress = getChecklistProgress(checklist?.Steps);
	const showProgress = !editMode && checklist && progress.total > 0;

	if (isLoading) {
		return (
			<Box className="heading-wrapper" sx={{ mb: 2 }}>
				<Box sx={{ p: 2 }}>
					<Skeleton variant="text" width="60%" height={36} />
					<Skeleton variant="text" width="80%" height={22} />
				</Box>
			</Box>
		);
	}

	if (!checklist) {
		return (
			<Box className="heading-wrapper" sx={{ mb: 2 }}>
				<Typography color="text.secondary">
					<Translate String="Procedure.Checklist.NoData">
						No checklist data available.
					</Translate>
				</Typography>
			</Box>
		);
	}

	if (editMode) {
		return <Box className="heading-wrapper">{children}</Box>;
	}

	return (
		<Box
			className="heading-wrapper checklist-view-header"
			sx={(t) => ({
				mb: headerCollapsed ? 1 : 2,
				px: { xs: 2, sm: 3 },
				pt: headerCollapsed ? 1.25 : 3.25,
				pb: headerCollapsed ? 1.25 : 2.5,
				flexShrink: 0,
				backgroundColor: t.palette.background.default,
				borderBottom: `1px solid ${alpha(t.palette.divider, 0.9)}`,
				overflow: "hidden",
				minWidth: 0,
			})}
		>
			<Box
				sx={{
					position: "relative",
					zIndex: 11,
					width: "100%",
					minWidth: 0,
					overflow: "hidden",
				}}
			>
				{headerCollapsed ? (
					/* Collapsed: single row = progress bar + tools */
					<Box
						className="checklist-header-top-row"
						sx={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "space-between",
							width: "100%",
							gap: 1,
						}}
					>
						{showProgress ? (
							<CompactProgressBar
								completed={progress.completed}
								total={progress.total}
								bodyFontSize={bodyFontSize}
								showLabel={!headerCollapsed}
							/>
						) : (
							<Box sx={{ flex: 1, minWidth: 0 }} />
						)}
						<Box
							className="checklist-header-tools-row"
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								alignItems: "center",
								flexShrink: 0,
							}}
						>
							{children}
						</Box>
					</Box>
				) : (
					<>
						{/* One row: checklist title (left) | all tools + nav (right) */}
						<Box
							className="checklist-header-top-row"
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								justifyContent: "space-between",
								width: "100%",
								mb: 1,
								gap: 2,
							}}
						>
							<Box
								className="heading-wrapper-container"
								sx={{ minWidth: 0, flex: "1 1 auto" }}
							>
								<Stack
									direction="row"
									alignItems="center"
									flexWrap="wrap"
									gap={1}
								>
									<Typography
										variant="h4"
										className="heading"
										sx={{
											fontWeight: 800,
											fontSize: `calc(1.4 * ${headingFontSize})`,
											lineHeight: 1.2,
											letterSpacing: "-0.025em",
											color: "text.primary",
										}}
									>
										{checklist?.Name}
									</Typography>
									<Chip
										size="small"
										label={
											checklist?.ImportedChecklist ? (
												<Translate String="Procedure.Checklist.Imported">
													Imported checklist
												</Translate>
											) : (
												<Translate String="Procedure.Checklist.Manual">
													Manual checklist
												</Translate>
											)
										}
										variant="outlined"
										sx={(theme) => ({
											borderRadius: 999,
											fontWeight: 700,
											fontSize: "0.75rem",
											borderColor: alpha(theme.palette.primary.main, 0.18),
											backgroundColor: alpha(theme.palette.background.paper, 0.72),
										})}
									/>
								</Stack>
							</Box>
							<Box
								className="checklist-header-tools-row"
								sx={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									flexShrink: 0,
								}}
							>
								{children}
							</Box>
						</Box>
						{!!checklist?.Ingress && (
							<Box
								sx={{
									typography: "body2",
									color: "text.secondary",
									fontSize: bodyFontSize,
									lineHeight: 1.7,
									mt: 1.25,
									px: { xs: 0, sm: 0.25 },
									maxWidth: "min(100%, 1200px)",
									"& p": { mb: 0 },
									"& a": {
										textDecorationThickness: "0.08em",
										textUnderlineOffset: "0.16em",
									},
								}}
							>
								<SATOSContentRender isNotProcedureRender content={checklist.Ingress ?? ""} />
							</Box>
						)}
						{showProgress && (
							<Box
								sx={{
									mt: 1.5,
									display: "flex",
									alignItems: "center",
									gap: 1,
									minWidth: 0,
								}}
							>
								<CompactProgressBar
									completed={progress.completed}
									total={progress.total}
									bodyFontSize={bodyFontSize}
									showLabel
								/>
							</Box>
						)}
					</>
				)}
			</Box>
			{!headerCollapsed && <Divider sx={{ mt: 2 }} />}
		</Box>
	);
};

/* Wrapper so toolbar and title sit above the sticky header’s ::after and stay clickable */

/* ────────────────────────────────────────────────────────── */
/* Header toolbar (navigation, edit, print, etc.)             */
/* ────────────────────────────────────────────────────────── */

interface ChecklistHeadingToolsProps {
	activeSchemeId: number | undefined;
	SetActiveSchemeId: (schemeId: number) => void;
	activeTools?: Set<HeaderTools>;
	checklistProcedureType: ChecklistProcedureType;
	editMode: boolean | undefined;
	setEditMode?: (editMode: boolean) => void;
}

const toolIconButtonSx = (theme: Theme) => ({
	color: theme.palette.text.secondary,
	"&:hover": {
		color: theme.palette.text.primary,
		backgroundColor: theme.palette.action.hover,
	},
	"&.Mui-disabled": {
		color: theme.palette.text.disabled,
	},
});

export const ChecklistHeadingTools: FC<ChecklistHeadingToolsProps> = (
	props,
) => {
	const theme = useTheme();
	const { id } = useParams<RouteParams>();
	const { pathname, search } = useLocation();
	const isProcedurePageRoute = /^\/procedure\/\d+/.test(pathname);
	/** Build search params for procedure/checklist return URL: preserve tab, folderId, improvementsSubTab from current URL, with defaults */
	const proceduresReturnSearch = (() => {
		if (!isProcedurePageRoute) return undefined;
		const sp = new URLSearchParams(search);
		sp.set("hide", "0");
		if (!sp.has("tab")) sp.set("tab", "browse");
		if (!sp.has("improvementsSubTab"))
			sp.set("improvementsSubTab", "newImprov");
		return sp.toString() || undefined;
	})();
	/** For Fullscreen: preserve tab, folderId, improvementsSubTab so Close can return to same state */
	const fullscreenSearch = (() => {
		const sp = new URLSearchParams(search);
		sp.set("hide", "1");
		return sp.toString() || undefined;
	})();
	const { fontScale, setFontScale } = useChecklistView();

	const { data: ProcedureDetails } = useFetchActiveProcedureDetails();
	const { data: checklist } = useFetchChecklist(props.activeSchemeId || 0);

	const { activeChildNode } = useHandleProcedureDetails(
		ProcedureDetails?.ProcedureDetailNodes,
		props.activeSchemeId,
	);

	const cycleFontScale = (direction: "up" | "down") => {
		const order: ChecklistFontScale[] = ["small", "medium", "large"];
		const i = order.indexOf(fontScale);
		if (direction === "up" && i < order.length - 1) setFontScale(order[i + 1]);
		if (direction === "down" && i > 0) setFontScale(order[i - 1]);
	};

	if (!props.activeTools) {
		return <></>;
	}

	return (
		<Stack
			direction="row"
			spacing={0.5}
			alignItems="center"
			className="heading-wrapper-tools checklist-header-tools--compact"
			data-tour="checklist-tools"
			sx={{
				position: "relative",
				zIndex: 12,
				pointerEvents: "auto",
				flexWrap: "wrap",
				justifyContent: "flex-end",
				width: "100%",
				maxWidth: "100%",
			}}
		>
			{props.activeTools.has("navigation") && (
				<ChecklistNavigationControls
					activeSchemeId={props.activeSchemeId}
					setActiveSchemeId={props.SetActiveSchemeId}
					compact
				/>
			)}
			{!props.editMode && (
				<>
					<Tooltip
						title={
							<Translate String="Checklist.Tools.TextSizeSmall">
								Smaller text
							</Translate>
						}
					>
						<span>
							<IconButton
								size="small"
								className="button-icon"
								aria-label="Smaller text"
								onClick={() => cycleFontScale("down")}
								disabled={fontScale === "small"}
								sx={toolIconButtonSx(theme)}
							>
								<TextDecreaseIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip
						title={
							<Translate String="Checklist.Tools.TextSizeLarge">
								Larger text
							</Translate>
						}
					>
						<span>
							<IconButton
								size="small"
								className="button-icon"
								aria-label="Larger text"
								onClick={() => cycleFontScale("up")}
								disabled={fontScale === "large"}
								sx={toolIconButtonSx(theme)}
							>
								<TextIncreaseIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</>
			)}

			{props.activeTools.has("fullscreen") && (
				<Tooltip
					title={
						<Translate String="Checklist.Tools.Fullscreen">
							Fullscreen
						</Translate>
					}
				>
					<IconButton
						size="small"
						component={SATOSLink}
						to="AccessProcedureNode"
						params={[id, props.activeSchemeId]}
						search={fullscreenSearch}
						className="button-icon"
						aria-label="Fullscreen"
						sx={toolIconButtonSx(theme)}
					>
						<FullscreenIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}

			<AdminProtected>
				<ChecklistHeadingToolEdit
					{...props}
					activeChildNode={activeChildNode}
					importedChecklist={
						checklist?.ImportedChecklist || activeChildNode?.ImportedChecklist
					}
					setEditMode={props.setEditMode!}
				/>
			</AdminProtected>

			{props.activeTools.has("print") && (
				<Tooltip
					title={<Translate String="Checklist.Tools.Print">Print</Translate>}
				>
					<IconButton
						size="small"
						component={SATOSLink}
						to="ChecklistPrint"
						params={[id, props.activeSchemeId]}
						serverSideRendering
						className="button-icon"
						aria-label="Print"
						sx={toolIconButtonSx(theme)}
					>
						<PrintIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}

			{props.activeTools.has("openInNewWindow") && (
				<Tooltip
					title={
						<Translate String="Checklist.Tools.OpenNew">
							Open in new window
						</Translate>
					}
				>
					<IconButton
						size="small"
						component={SATOSLink}
						to="AccessProcedureNode"
						params={[id, props.activeSchemeId]}
						search={fullscreenSearch}
						target="_blank"
						className="button-icon"
						aria-label="Open in new window"
						sx={toolIconButtonSx(theme)}
					>
						<NewWindowIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}

			{props.activeTools.has("close") &&
				props.checklistProcedureType === "procedure" && (
					<Tooltip
						title={<Translate String="Checklist.Tools.Close">Close</Translate>}
					>
						<IconButton
							size="small"
							component={SATOSLink}
							to={
								isProcedurePageRoute &&
								id &&
								validSchemeId(props.activeSchemeId)
									? `/procedure/${id}/checklist/${props.activeSchemeId}`
									: "tremeny0"
							}
							search={proceduresReturnSearch}
							className="button-icon"
							aria-label="Close"
							sx={toolIconButtonSx(theme)}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}

			{props.activeTools.has("close") &&
				props.checklistProcedureType === "materialhandling" && (
					<Tooltip
						title={<Translate String="Checklist.Tools.Close">Close</Translate>}
					>
						<IconButton
							size="small"
							component={SATOSLink}
							to="MaterialHandlingMyPage"
							className="button-icon"
							aria-label="Close"
							sx={toolIconButtonSx(theme)}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
		</Stack>
	);
};

interface ChecklistHeadingToolEditProps {
	activeSchemeId: number | undefined;
	SetActiveSchemeId: (schemeId: number) => void;
	activeTools?: Set<HeaderTools>;
	checklistProcedureType: ChecklistProcedureType;
	editMode: boolean | undefined;
	activeChildNode: ProcedureDetailNode | undefined;
	importedChecklist: boolean | undefined;
	setEditMode: (editMode: boolean) => void;
}

const ChecklistHeadingToolEdit: FC<ChecklistHeadingToolEditProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const [redirect, setRedirect] = useState(false);
	const [procedureId, setProcedureId] = useState<number>(0);
	const [nodeSchemeId, setNodeSchemeId] = useState<number>(0);

	const { id } = useParams<RouteParams>();

	const navigate = useNavigate();
	if (redirect) {
		return (
			<Navigate
				to={`/procedure/${procedureId}/node/${nodeSchemeId}/CompareVersions`}
			/>
		);
	}

	if (
		!props.activeTools ||
		!props.activeTools.has("edit") ||
		!validSchemeId(props.activeSchemeId)
	) {
		return <></>;
	}

	if (props.checklistProcedureType === "procedure") {
		return (
			<Tooltip
				title={<Translate String="Checklist.Tools.Edit">Edit</Translate>}
			>
				<IconButton
					size="small"
					onClick={(e) => {
						e.preventDefault();
						navigate(`edit`);
					}}
					className="button-icon"
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.Checklist.L1056C17.Edit", "Edit")}
					sx={toolIconButtonSx(theme)}
				>
					{props.editMode ? (
						<CloseOutlinedIcon fontSize="small" />
					) : (
						<EditIcon fontSize="small" />
					)}
				</IconButton>
			</Tooltip>
		);
	}

	if (props.checklistProcedureType === "materialhandling") {
		return (
			<Tooltip
				title={<Translate String="Checklist.Tools.Edit">Edit</Translate>}
			>
				<IconButton
					size="small"
					component={SATOSLink}
					sx={toolIconButtonSx(theme)}
					to="EditMaterialHandling"
					params={[id]}
					className="button-icon"
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.Checklist.L1081C17.Edit", "Edit")}
				>
					<EditIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		);
	}

	return null;
};
